/** Resultado da persistência do roaming settings. */
export interface FlushResult {
  ok: boolean;
  error?: { code?: number; message?: string };
}

/**
 * Chaves sacrificáveis quando o `roamingSettings` estoura a cota (~32 KB).
 *
 */
const SHEDDABLE_KEYS = ["helpitech_tpl_cache", "helpitech_tpl_at"];

let current: Promise<FlushResult> | null = null;
let pending: Promise<FlushResult> | null = null;

function saveOnce(): Promise<FlushResult> {
  return new Promise((resolve) => {
    Office.context.roamingSettings.saveAsync((result) => {
      resolve(
        result.status === Office.AsyncResultStatus.Succeeded
          ? { ok: true }
          : { ok: false, error: { code: result.error?.code, message: result.error?.message } }
      );
    });
  });
}

async function saveWithShedding(): Promise<FlushResult> {
  const first = await saveOnce();
  if (first.ok) return first;

  console.warn(
    `[storage] roamingSettings.saveAsync falhou (code=${first.error?.code}, msg=${first.error?.message}) - ` +
    "descartando o cache de templates e tentando de novo."
  );

  try {
    for (const key of SHEDDABLE_KEYS) Office.context.roamingSettings.remove(key);
  } catch {
  }

  return saveOnce();
}

/**
 * Persiste o `roamingSettings` do Outlook, serializando as chamadas e
 * descartando o cache de templates se a cota estourar.
 *
 * @returns Resultado final da gravação (após o eventual descarte e retry).
 *
 * @example
 * ```typescript
 * const { ok, error } = await flushRoaming();
 * if (!ok) toast.error("Erro ao salvar: " + error?.message);
 * ```
 */
export function flushRoaming(): Promise<FlushResult> {
  if (typeof Office === "undefined" || !Office.context?.roamingSettings) {
    return Promise.resolve({ ok: true });
  }

  if (!current) {
    current = saveWithShedding().finally(() => { current = null; });
    return current;
  }

  // Já há uma gravação agendada para depois da corrente: todos os chamadores
  // desta janela compartilham essa mesma gravação.
  if (pending) return pending;

  pending = current.then(() => {
    pending = null;
    return flushRoaming();
  });
  return pending;
}

/**
 * Lê um valor do roaming settings do Outlook com fallback para localStorage.
 *
 * @param key - A chave do roaming setting.
 * @param localStorageKey - A chave alternativa no localStorage (opcional).
 * @returns O valor armazenado ou `null` se não encontrado.
 *
 * @example
 * ```typescript
 * const userInfo = getFromRoamingOrLocal("helpitech_user_info", "user_info");
 * ```
 */
export function getFromRoamingOrLocal(
  key: string,
  localStorageKey?: string
): string | null {
  try {
    if (typeof Office !== "undefined" && Office.context?.roamingSettings) {
      const roaming = Office.context.roamingSettings.get(key);
      if (roaming && roaming !== "" && roaming !== "null") {
        return typeof roaming === "string" ? roaming : JSON.stringify(roaming);
      }
    }
  } catch {
    // Office API indisponível
  }

  if (localStorageKey) {
    const local = localStorage.getItem(localStorageKey);
    if (local && local !== "null") return local;
  }

  return null;
}

/**
 * Salva um valor no roaming settings do Outlook com fallback para localStorage.
 *
 * @param key - A chave do roaming setting.
 * @param value - O valor a ser armazenado.
 * @param localStorageKey - A chave alternativa no localStorage (opcional).
 *
 * @example
 * ```typescript
 * setRoamingSetting("new_mail", "template_1", "new_mail");
 * ```
 */
export function setRoamingSetting(
  key: string,
  value: string,
  localStorageKey?: string
): void {
  try {
    if (typeof Office !== "undefined" && Office.context?.roamingSettings) {
      Office.context.roamingSettings.set(key, value);
      void flushRoaming();
    }
  } catch {
    // Office API indisponível
  }

  try {
    if (localStorageKey) {
      localStorage.setItem(localStorageKey, value);
    }
  } catch {
    // localStorage indisponível
  }
}

/**
 * Remove um valor do roaming settings e do localStorage.
 *
 * @param roamingKey - A chave no roaming settings.
 * @param localStorageKey - A chave no localStorage (opcional).
 *
 * @example
 * ```typescript
 * removeFromStorage("helpitech_user_info", "user_info");
 * ```
 */
export function removeFromStorage(
  roamingKey: string,
  localStorageKey?: string
): void {
  try {
    if (typeof Office !== "undefined" && Office.context?.roamingSettings) {
      Office.context.roamingSettings.remove(roamingKey);
      void flushRoaming();
    }
  } catch {
    // Office API indisponível
  }

  try {
    if (localStorageKey) {
      localStorage.removeItem(localStorageKey);
    }
  } catch {
    // localStorage indisponível
  }
}
